/** View-model builders for Cargo Heating onboarding details (API-driven). */

import {
  extractOnboardingModulesFromRow,
} from 'src/app/shared/helpers/onboarding-modules.util';
import {
  buildLiquidCargoDynamicTable,
  LIQUID_CARGO_EMPTY_MESSAGE,
  resolveLiquidCargoRows,
} from './cargo-heating-liquid-cargo.util';

export interface CargoOnboardingSummary {
  vesselName: string;
  imo: string;
  clientId: string;
  segment: string;
  vesselType: string;
  tankerType: string;
}

export interface CargoOnboardingKvRow {
  label: string;
  value: string;
  moduleKeys?: string[];
}

export interface CargoOnboardingKvSection {
  id: string;
  title: string;
  rows: CargoOnboardingKvRow[];
  spanFull?: boolean;
}

export interface CargoOnboardingTableSection {
  id: string;
  title: string;
  headers: string[];
  rows: string[][];
  spanFull?: boolean;
  emptyMessage?: string;
}

export interface CargoOnboardingUploadedFile {
  field: string;
  documentType: string;
  fileName: string;
  s3Url: string;
  mimeType?: string;
  sizeBytes?: number;
}

export interface CargoOnboardingDashboardView {
  summary: CargoOnboardingSummary;
  cardSections: CargoOnboardingKvSection[];
  tableSections: CargoOnboardingTableSection[];
  uploadedFiles: CargoOnboardingUploadedFile[];
}

const UPLOAD_FIELD_LABELS: Record<string, string> = {
  boiler_graph: 'Boiler Performance Graph',
  steam_table: 'Steam Consumption Table',
  liquid_cargo: 'Liquid Cargo / Heating Table',
  boiler_graph_json: 'Boiler Graph JSON',
};

/** Null-safe display: preserves 0 and false; only null/undefined/empty string → — */
export function displayCargoValue(value: unknown): string {
  if (value === null || value === undefined) {
    return '—';
  }
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }
  if (typeof value === 'number') {
    return Number.isFinite(value) ? String(value) : '—';
  }
  if (typeof value === 'string') {
    return value.trim() === '' ? '—' : value.trim();
  }
  if (Array.isArray(value)) {
    if (!value.length) {
      return '—';
    }
    return value
      .map((item) => displayCargoValue(item))
      .filter((item) => item !== '—')
      .join(', ') || '—';
  }
  if (typeof value === 'object') {
    return formatCenterOfGravity(value as Record<string, unknown>);
  }
  return String(value);
}

export function formatCargoApiValue(value: unknown): string {
  return displayCargoValue(value);
}

function formatCenterOfGravity(value: Record<string, unknown> | null): string {
  if (!value || typeof value !== 'object') {
    return '—';
  }

  const entries: Array<{ label: string; raw: unknown }> = [
    {
      label: 'LCG',
      raw:
        value['L.C.G. FROM MIDSHIP(M)'] ??
        value['lcg_from_midship_m'] ??
        value['lcgFromMidshipM'],
    },
    {
      label: 'TCG',
      raw:
        value['T.C.G. FROM C.L.(M)'] ??
        value['tcg_from_cl_m'] ??
        value['tcgFromClM'],
    },
    {
      label: 'VCG',
      raw:
        value['V.C.G. FROM B.L.(M)'] ??
        value['vcg_from_bl_m'] ??
        value['vcgFromBlM'],
    },
  ];

  const parts = entries
    .map(({ label, raw }) => {
      if (raw === null || raw === undefined || raw === '') {
        return null;
      }
      return `${label}: ${raw}`;
    })
    .filter((part): part is string => !!part);

  return parts.length ? parts.join('; ') : '—';
}

function pickApiValue(
  row: Record<string, unknown>,
  ...keys: string[]
): string {
  for (const key of keys) {
    const value = row[key];
    if (value === null || value === undefined) {
      continue;
    }
    if (typeof value === 'string' && value.trim() === '') {
      continue;
    }
    return displayCargoValue(value);
  }
  return '—';
}

function formatPipelineArray(values: unknown): string {
  if (!Array.isArray(values)) {
    return '—';
  }
  if (!values.length) {
    return '—';
  }

  const formatted = values
    .map((item) => {
      if (item === null || item === undefined || item === '') {
        return null;
      }
      return String(item);
    })
    .filter((item): item is string => item !== null);

  return formatted.length ? formatted.join(', ') : '—';
}

function isHttpsUrl(url: string): boolean {
  return /^https?:\/\//i.test(url);
}

function resolveBestS3Url(...urls: Array<string | undefined>): string {
  const candidates = urls
    .map((url) => String(url ?? '').trim())
    .filter(Boolean);
  const https = candidates.find((url) => isHttpsUrl(url));
  if (https) {
    return https;
  }
  return candidates.find((url) => !url.startsWith('s3://')) ?? '';
}

function formatFileSize(bytes: unknown): string {
  const size = Number(bytes);
  if (!Number.isFinite(size) || size < 0) {
    return '—';
  }
  if (size < 1024) {
    return `${size} B`;
  }
  if (size < 1024 * 1024) {
    return `${(size / 1024).toFixed(1)} KB`;
  }
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}

function normalizeUploadedFiles(
  row: Record<string, unknown>,
): CargoOnboardingUploadedFile[] {
  const raw = row['uploaded_files'] ?? row['uploadedFiles'];
  if (!Array.isArray(raw)) {
    return [];
  }

  const deduped = new Map<string, CargoOnboardingUploadedFile>();

  raw.forEach((item) => {
    if (!item || typeof item !== 'object') {
      return;
    }
    const file = item as Record<string, unknown>;
    const field = String(file['field'] ?? '').trim();
    const fileName = String(file['file_name'] ?? file['fileName'] ?? '').trim();
    const s3Url = String(file['s3_url'] ?? file['s3Url'] ?? '').trim();
    if (!field && !fileName && !s3Url) {
      return;
    }

    const key = `${field}|${fileName}`;
    const existing = deduped.get(key);
    const resolvedUrl = resolveBestS3Url(existing?.s3Url, s3Url);
    const mimeType = String(file['mime_type'] ?? file['mimeType'] ?? existing?.mimeType ?? '').trim();
    const sizeRaw = file['size_bytes'] ?? file['sizeBytes'] ?? existing?.sizeBytes;

    deduped.set(key, {
      field,
      documentType: UPLOAD_FIELD_LABELS[field] ?? (field || 'Document'),
      fileName: fileName || '—',
      s3Url: resolvedUrl,
      mimeType: mimeType || undefined,
      sizeBytes:
        sizeRaw !== null && sizeRaw !== undefined && sizeRaw !== ''
          ? Number(sizeRaw)
          : existing?.sizeBytes,
    });
  });

  return Array.from(deduped.values()).filter((file) => file.fileName !== '—' || file.s3Url);
}

export function extractUploadedFilesFromRow(
  row: Record<string, unknown>,
): CargoOnboardingUploadedFile[] {
  return normalizeUploadedFiles(row);
}

function buildPipelineSectionsTable(
  row: Record<string, unknown>,
): CargoOnboardingTableSection {
  const sections = row['pipeline_sections'] ?? row['pipelineSections'];
  const rows: string[][] = [];

  if (Array.isArray(sections)) {
    sections.forEach((item) => {
      if (!item || typeof item !== 'object') {
        return;
      }
      const section = item as Record<string, unknown>;
      rows.push([
        pickApiValue(section, 'segment_name', 'segmentName'),
        formatPipelineArray(
          section['length_values_m'] ?? section['length_m'] ?? section['lengthValuesM'],
        ),
        formatPipelineArray(
          section['pipeline_thickness_values_mm'] ??
            section['pipeline_thickness_mm'] ??
            section['pipelineThicknessValuesMm'],
        ),
        pickApiValue(section, 'steam_insulation', 'steamInsulation'),
      ]);
    });
  }

  return {
    id: 'pipeline-sections',
    title: 'Pipeline Sections',
    headers: ['Segment Name', 'Length Values (m)', 'Thickness Values (mm)', 'Steam Insulation'],
    rows,
  };
}

function buildTanksTable(row: Record<string, unknown>): CargoOnboardingTableSection {
  const tanks = row['tanks'] ?? row['heating_tanks'];
  const rows: string[][] = [];

  if (Array.isArray(tanks)) {
    tanks.forEach((item, index) => {
      if (!item || typeof item !== 'object') {
        return;
      }
      const tank = item as Record<string, unknown>;
      rows.push([
        String(index + 1),
        pickApiValue(tank, 'tank_name', 'name', 'tankName'),
        pickApiValue(tank, 'capacity_m3', 'capacity', 'capacityM3'),
        pickApiValue(tank, 'heating_surface_m2', 'heatingSurfaceM2', 'heating_surface'),
        pickApiValue(
          tank,
          'steam_consumption_kgh',
          'steam_consumption_kg_hr',
          'max_steam',
          'maxSteam',
          'max_steam_consumption_kg_hr',
        ),
      ]);
    });
  }

  return {
    id: 'tank-heating-grid',
    title: 'Tank / Heating Grid Information',
    headers: [
      'Sr. No.',
      'Tank Name',
      'Capacity (m³)',
      'Heating Surface (m²)',
      'Steam Consumption (kg/h)',
    ],
    rows,
    spanFull: true,
  };
}

function buildLiquidCargoTable(row: Record<string, unknown>): CargoOnboardingTableSection {
  const liquidBuilt = buildLiquidCargoDynamicTable(resolveLiquidCargoRows(row));
  const liquidCargoApplied =
    row['liquid_cargo_applied'] === true || row['liquidCargoApplied'] === true;

  return {
    id: 'liquid-cargo-table',
    title: 'Liquid Cargo Table',
    headers: liquidBuilt?.headers ?? [],
    rows: liquidBuilt?.rows ?? [],
    spanFull: true,
    emptyMessage: liquidBuilt?.rows.length
      ? undefined
      : liquidCargoApplied
        ? LIQUID_CARGO_EMPTY_MESSAGE
        : 'Liquid cargo is not applicable for this vessel.',
  };
}

export function buildCargoOnboardingDashboardView(
  row: Record<string, unknown>,
): CargoOnboardingDashboardView {
  const summary: CargoOnboardingSummary = {
    vesselName: pickApiValue(row, 'vessel_name', 'vesselName'),
    imo: pickApiValue(row, 'imo', 'vessel_imo', 'imo_number'),
    clientId: pickApiValue(row, 'client_id', 'clientId'),
    segment: pickApiValue(row, 'segment', 'vessel_segment'),
    vesselType: pickApiValue(row, 'vessel_type', 'vesselType'),
    tankerType: pickApiValue(row, 'tanker_type', 'tankerType'),
  };

  const cardSections: CargoOnboardingKvSection[] = [
    {
      id: 'heating-configuration',
      title: 'Heating Configuration',
      rows: [
        { label: 'Heating Arrangement', value: pickApiValue(row, 'heating_arrangement', 'heatingArrangement') },
        { label: 'Number of Tanks', value: pickApiValue(row, 'number_of_tanks', 'numberOfTanks') },
        { label: 'Steam Pipes Insulation', value: pickApiValue(row, 'steam_pipes_insulation', 'steamPipesInsulation') },
        { label: 'EGCS Scrubber', value: pickApiValue(row, 'egcs_scrubber', 'egcsScrubber') },
        { label: 'Quick Onboarding', value: displayCargoValue(row['quick_onboarding'] ?? row['quickOnboarding']) },
        {
          label: 'Onboarding Modules',
          value: '',
          moduleKeys: extractOnboardingModulesFromRow(row),
        },
      ],
    },
    {
      id: 'boiler-performance',
      title: 'Boiler Performance',
      rows: [
        { label: 'Boiler Make', value: pickApiValue(row, 'boiler_make', 'boilerMake') },
        { label: 'Number of Boilers', value: pickApiValue(row, 'number_of_boilers', 'numberOfBoilers') },
        { label: 'Boiler Capacity (kg/hr steam)', value: pickApiValue(row, 'boiler_capacity', 'boilerCapacity') },
        {
          label: 'Aux Boiler Fuel Consumption (kg/hr)',
          value: pickApiValue(row, 'aux_boiler_fuel_consumption_kg_hr', 'auxBoilerFuelConsumptionKgHr'),
        },
        { label: 'Max Efficiency (%)', value: pickApiValue(row, 'max_efficiency_percent', 'maxEfficiencyPercent') },
        { label: 'Steam Production (kg/hr)', value: pickApiValue(row, 'steam_production_kg_hr', 'steamProductionKgHr') },
        { label: 'Boiler Load (%)', value: pickApiValue(row, 'boiler_load_percentage', 'boilerLoadPercentage') },
        { label: 'Deck Steam Pressure', value: pickApiValue(row, 'deck_steam_pressure', 'deckSteamPressure') },
        { label: 'AE Boiler Consumption (MT)', value: pickApiValue(row, 'ae_boiler_consumption_mt', 'aeBoilerConsumptionMt') },
        {
          label: 'Deck Heat Exchanger Bunker Consumption (MT)',
          value: pickApiValue(
            row,
            'deck_heat_exchanger_bunker_consumption_mt',
            'deckHeatExchangerBunkerConsumptionMt',
          ),
        },
        { label: 'Deck Heater Capacity (kW)', value: pickApiValue(row, 'deck_heater_capacity_kw', 'deckHeaterCapacityKw') },
      ],
    },
    {
      id: 'composite-steam',
      title: 'Composite Boiler & Steam Output',
      rows: [
        {
          label: 'Composite Boiler Consumption Per Day',
          value: pickApiValue(row, 'composite_boiler_consumption_per_day', 'compositeBoilerConsumptionPerDay'),
        },
        {
          label: 'Composite Boiler Fuel Consumption (kg/hr)',
          value: pickApiValue(row, 'composite_boiler_fuel_consumption_kg_hr', 'compositeBoilerFuelConsumptionKgHr'),
        },
        {
          label: 'Steam Output – Oil Fired Section (kg/hr)',
          value: pickApiValue(row, 'steam_output_oil_fired_section_kg_hr', 'steamOutputOilFiredSectionKgHr'),
        },
        {
          label: 'Steam Output – Exhaust Section ME Motor (kg/hr)',
          value: pickApiValue(row, 'steam_output_exhaust_section_me_motor_kg_hr', 'steamOutputExhaustSectionMeMotorKgHr'),
        },
        {
          label: 'Steam Output – Exhaust Section AE Motor (kg/hr)',
          value: pickApiValue(row, 'steam_output_exhaust_section_ae_motor_kg_hr', 'steamOutputExhaustSectionAeMotorKgHr'),
        },
      ],
    },
    {
      id: 'egb-economizer',
      title: 'EGB / Economizer',
      rows: [
        { label: 'EGB Capacity (kg/hr)', value: pickApiValue(row, 'egb_capacity_kg_hr', 'egbCapacityKgHr') },
        {
          label: 'Exhaust Gas Quantity (kg/hr)',
          value: pickApiValue(row, 'egb_exhaust_gas_quantity_kg_hr', 'egbExhaustGasQuantityKgHr'),
        },
        {
          label: 'Gas Temperature Inlet (°C)',
          value: pickApiValue(row, 'egb_gas_temp_inlet_deg_c', 'egbGasTempInletDegC'),
        },
        {
          label: 'Gas Temperature Outlet (°C)',
          value: pickApiValue(row, 'egb_gas_temp_outlet_deg_c', 'egbGasTempOutletDegC'),
        },
      ],
    },
    {
      id: 'pipeline-summary',
      title: 'Pipeline Summary',
      rows: [
        { label: 'Total Pipeline Length (m)', value: pickApiValue(row, 'pipeline_length_m', 'pipelineLengthM') },
        { label: 'Pipeline Thickness (mm)', value: pickApiValue(row, 'pipeline_thickness_mm', 'pipelineThicknessMm') },
        { label: 'Main Line Length', value: pickApiValue(row, 'pipeline_main_line_length_m', 'pipelineMainLineLengthM') },
        { label: 'Main Line Thickness', value: pickApiValue(row, 'pipeline_main_line_thickness_mm', 'pipelineMainLineThicknessMm') },
        { label: 'Branch Line Length', value: pickApiValue(row, 'pipeline_branch_line_length_m', 'pipelineBranchLineLengthM') },
        { label: 'Branch Line Thickness', value: pickApiValue(row, 'pipeline_branch_line_thickness_mm', 'pipelineBranchLineThicknessMm') },
        { label: 'Engine Room Length', value: pickApiValue(row, 'pipeline_in_engine_room_length_m', 'pipelineInEngineRoomLengthM') },
        { label: 'Engine Room Thickness', value: pickApiValue(row, 'pipeline_in_engine_room_thickness_mm', 'pipelineInEngineRoomThicknessMm') },
        { label: 'Main Line Steam Insulation', value: pickApiValue(row, 'steam_insulation_main_line', 'steamInsulationMainLine') },
        { label: 'Branch Line Steam Insulation', value: pickApiValue(row, 'steam_insulation_branch_line', 'steamInsulationBranchLine') },
        {
          label: 'Engine Room Steam Insulation',
          value: pickApiValue(row, 'steam_insulation_in_engine_room', 'steamInsulationInEngineRoom'),
        },
      ],
    },
    {
      id: 'onboarding-metadata',
      title: 'Onboarding Metadata',
      rows: [
        { label: 'Boiler Graph Applied', value: displayCargoValue(row['boiler_graph_applied'] ?? row['boilerGraphApplied']) },
        { label: 'Steam Table Applied', value: displayCargoValue(row['steam_table_applied'] ?? row['steamTableApplied']) },
        { label: 'Liquid Cargo Applied', value: displayCargoValue(row['liquid_cargo_applied'] ?? row['liquidCargoApplied']) },
        { label: 'Liquid Cargo Fill Note', value: pickApiValue(row, 'liquid_cargo_fill_note', 'liquidCargoFillNote') },
        { label: 'Submitted At', value: pickApiValue(row, 'submitted_at', 'submittedAt') },
        { label: 'Updated At', value: pickApiValue(row, 'updated_at', 'updatedAt') },
        { label: 'Created At', value: pickApiValue(row, 'created_at', 'createdAt') },
      ],
    },
  ];

  const liquidCargoTable = buildLiquidCargoTable(row);

  return {
    summary,
    cardSections,
    tableSections: [
      buildPipelineSectionsTable(row),
      buildTanksTable(row),
      liquidCargoTable,
    ],
    uploadedFiles: normalizeUploadedFiles(row),
  };
}

export function formatUploadedFileSize(bytes?: number): string {
  return formatFileSize(bytes);
}

export function isPreviewableUploadedFileUrl(url: string): boolean {
  return isHttpsUrl(url);
}
